import { Outlet, useLocation, useNavigate } from 'react-router';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import React from 'react';
import { useGuestProductFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { Add, Badge, CloudDownload, QrCode2 } from '@mui/icons-material';
import {
    RequestStatus,
    ServerRequestState,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { FiltersBox } from '../shared-mui-components/filter/FiltersBox.tsx';
import {
    OperatorProductListOutlet,
    ProductSearchResult,
    ProductsSearchTooltip,
    useOperatorProductListContext,
} from './OperatorProducts.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import {
    EmptyResults,
    FetchError,
    ResultLimitAlert,
} from '../shared-mui-components/ListMessages.tsx';
import { OperatorProductStatusIcon } from './OperatorProductState.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { OperatorProductDetailModal } from './OperatorProductDetailModal.tsx';
import { makeSearchQueryWithFilters } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';

export function OperatorGuestProducts() {
    const { search } = useLocation();
    const { operatorData, operatorExternalId } = useOperatorContext();
    const { language } = useLanguage();
    const paramsWithLanguage = React.useMemo(() => {
        const params = new URLSearchParams(search);
        params.append('language', language);
        return params;
    }, [search, language]);
    const navigate = useNavigate();

    const {
        filterConfig,
        activeFilters,
        listState,
        refetchList,
        searchText,
        filterConfigState,
    } = useGuestProductFilterConfiguration();

    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <Localized
                        de="Gastprodukte"
                        fr="Produits hôtes"
                        it="Prodotti ospiti"
                        en="Guest products"
                    />
                }
                icon={Badge}
                headerButtons={
                    <Stack
                        direction="row"
                        spacing={3}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {listState.status === RequestStatus.SUCCESS &&
                            listState.data.results.length > 0 && (
                                <Button
                                    variant="outlined"
                                    startIcon={<CloudDownload />}
                                    href={urlWithOperatorExternalId(
                                        `/ui-api/operator-account/guest-product/export?${paramsWithLanguage.toString()}`,
                                        operatorExternalId,
                                    )}
                                >
                                    <Localized
                                        de="Exportieren"
                                        fr="Exporter"
                                        it="Esporta"
                                        en="Export"
                                    />
                                </Button>
                            )}
                        {operatorData.hasActiveGuestDayPassTemplateOrStack && (
                            <Button
                                variant="outlined"
                                startIcon={<QrCode2 />}
                                onClick={() =>
                                    navigate('../guest-day-pass-overview')
                                }
                            >
                                <Localized
                                    de="Tagespässe"
                                    fr="Pass journaliers"
                                    it="Pass giornalieri"
                                    en="Daily passes"
                                />
                            </Button>
                        )}
                        {operatorData.hasActiveGuestReservationTemplate && (
                            <Button
                                variant="contained"
                                color={'primary'}
                                startIcon={<Add />}
                                onClick={() =>
                                    navigate('add-guest-reservation')
                                }
                            >
                                <Localized
                                    de="ERFASSEN"
                                    fr="SAISIR"
                                    it="REGISTRA"
                                    en="CAPTURE"
                                />
                            </Button>
                        )}
                    </Stack>
                }
            />
            <FiltersBox
                searchText={searchText}
                activeFilters={activeFilters}
                filterConfig={filterConfig}
                requestState={listState}
                refetch={refetchList}
                pending={filterConfigState.status === RequestStatus.PENDING}
                tooltip={<ProductsSearchTooltip />}
            />
            <OperatorGuestProductsList
                listState={listState}
                refetchList={refetchList}
            />
            <OperatorProductListOutlet context={{ refetchList }} />
        </MainContent>
    );
}

function OperatorGuestProductsList({
    listState,
    refetchList,
}: {
    listState: ServerRequestState<ProductSearchResult, object>;
    refetchList: () => void;
}) {
    const { search } = useLocation();
    const navigate = useNavigate();
    return (
        <OperatorAsyncLoadedSection
            requestState={listState}
            pendingLoaderPreset={LoadingSpinnerPresets.FillAllSpaceAndCenter}
            render={results => {
                const isEmpty = results.results.length === 0;
                if (isEmpty) {
                    return <EmptyResults />;
                }

                return (
                    <>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Status"
                                                fr="État"
                                                it="Stato"
                                                en="State"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Bezeichnung"
                                                fr="Désignation"
                                                it="Denominazione"
                                                en="Denomination"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Beginn"
                                                fr="Début"
                                                it="Inizio"
                                                en="Start"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Ende"
                                                fr="Fin"
                                                it="Fine"
                                                en="End"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Erstellt am"
                                                fr="Créé le"
                                                it="Creato il"
                                                en="Created at"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.results.map(product => (
                                        <TableRow
                                            key={product.contractExternalId}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                navigate(
                                                    `${product.contractExternalId}/detail${search}`,
                                                )
                                            }
                                        >
                                            <TableCell>
                                                <OperatorProductStatusIcon
                                                    status={product.status}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Localized {...product.name} />
                                            </TableCell>
                                            <TableCell>
                                                {Formatter.dayMonthYearHourMinute(
                                                    DateTime.fromISO(
                                                        product.validFrom,
                                                    ),
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {product.validTo
                                                    ? Formatter.dayMonthYearHourMinute(
                                                          DateTime.fromISO(
                                                              product.validTo,
                                                          ),
                                                      )
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {Formatter.dayMonthYearHourMinute(
                                                    DateTime.fromISO(
                                                        product.createdAt,
                                                    ),
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <ResultLimitAlert
                                eof={results.eof}
                                numberOfResults={results.results.length}
                                hasExport={true}
                            />
                        </TableContainer>
                    </>
                );
            }}
            renderError={() => <FetchError refetchList={refetchList} />}
        />
    );
}

export function OperatorGuestProductDetail() {
    const { activeFilters, searchText } = useGuestProductFilterConfiguration();
    const { refetchList } = useOperatorProductListContext();

    return (
        <>
            <OperatorProductDetailModal
                backUrl={`..${makeSearchQueryWithFilters(searchText, activeFilters)}`}
                onProductChange={refetchList}
            />
            <Outlet />
        </>
    );
}
